import type { ReactNode } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

interface Props {
  id: string
  /** Announced to screen readers and used as the handle's label. */
  label: string
  /** Right-hand controls. Kept a sibling of the content, not nested inside
   *  it, so they can be pinned to the right edge of the row. */
  actions?: ReactNode
  /** True while the row is an edit form, which wants the full width. */
  editing?: boolean
  children: ReactNode
}

/**
 * One draggable row, shared by the project list and the tag list.
 *
 * The drag listeners go on a dedicated handle, not the whole row: the row
 * also carries buttons, and a row-wide drag target would swallow their
 * clicks.
 */
export function SortableRow({ id, label, actions, editing, children }: Props) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } =
    useSortable({ id })

  const classes = ['row', isDragging && 'dragging', editing && 'editing'].filter(Boolean).join(' ')

  return (
    <li
      ref={setNodeRef}
      className={classes}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        className="drag-handle"
        aria-label={`Reorder ${label}`}
        {...attributes}
        {...listeners}
      >
        <span aria-hidden="true">⠿</span>
      </button>

      <div className="row-body">{children}</div>

      {actions && <div className="row-actions">{actions}</div>}
    </li>
  )
}
